import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';

import { Button, Flex } from '@revenuemarket/cds';
import { ResponseStatus } from '@revenuemarket/http';
import { Toast } from '@revenuemarket/ui';
import Input, { TextArea } from '@revenuemarket/ui/Input/v2';
import { InputRef } from 'antd';
import { GroupTreeItem } from '~apis/custom-label';
import { FlattenTreeResult } from '~components/ui/HorizontalTreeTable/Table';
import { TreeNodeId } from '~components/ui/HorizontalTreeTable/utils';
import { useAddGroupSpecificLabel } from '~hooks/queries/custom-labels/query';
import { useDemoAwareCompanyId } from '~hooks/useDemoAwareCompanyId';
import { MIXPANEL_EVENT } from '~libs/mixpanel/event';
import { mixpanel } from '~pages/_app';

import {
  CATEGORY_DESCRIPTION_MAX_LENGTH,
  CATEGORY_DESCRIPTION_MAX_LENGTH_INFO,
} from './CategoryDescriptionTextArea';
import { CATEGORY_NAME_MAX_LENGTH, CATEGORY_NAME_MAX_LENGTH_INFO } from './CategoryNameInput';
import InfoItem from './InfoItem';

interface NewInfoProps extends GroupTreeItem {
  onCancel: () => void;
  depth: number;
  parentId: TreeNodeId | null;
  flattenedTree: FlattenTreeResult<GroupTreeItem>[];
  onSuccess?: (treeKey: string) => void;
}

const GroupNewInfo: React.FC<NewInfoProps> = ({
  onCancel,
  onSuccess,
  parentId,
  flattenedTree,
  depth,
  ...props
}) => {
  const inputRef = useRef<InputRef>(null);
  const submittingRef = useRef(false);
  const { companyId } = useDemoAwareCompanyId();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const { mutateAsync: addGroupSpecificLabel, isLoading } = useAddGroupSpecificLabel();
  const parentIdParsed = parentId as string;

  const onAdd = useCallback(async () => {
    if (name.length === 0 || isLoading || submittingRef.current) return;
    submittingRef.current = true;
    try {
      const siblingNames = flattenedTree
        .find((item) => item.key === parentId)
        ?.children.map((i) => i.value.name);
      if (siblingNames?.includes(name)) {
        Toast.error('동일한 이름의 항목이 이미 존재합니다.');
        return;
      }
      const response = await addGroupSpecificLabel({
        companyId,

        request: {
          name,
          description,
          parentId: depth === 0 ? undefined : Number(parentIdParsed),
        },
      });
      if (response.Status === ResponseStatus.SUCCESS) {
        Toast.success('항목이 추가되었습니다.');
        mixpanel.track(MIXPANEL_EVENT.GROUP.CREATE_DETAILED_GROUP, {
          depth: depth,
        });
        mixpanel.track(MIXPANEL_EVENT.ENGAGEMENT.DEFAULT, {
          type: 'CREATE_DETAILED_GROUP',
        });
        onSuccess?.(response.Data?.data?.categoryTreeKey ?? '');
      } else {
        Toast.error('항목 추가에 실패했습니다.');
      }
    } finally {
      submittingRef.current = false;
    }
  }, [
    addGroupSpecificLabel,
    companyId,
    depth,
    flattenedTree,
    name,
    onSuccess,
    parentIdParsed,
    isLoading,
    description,
    parentId,
  ]);

  // // 처음 들어왔을 때 input에 focus
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSubmit = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      onAdd();
    },
    [onAdd],
  );

  return (
    <form onSubmit={handleSubmit} style={{ width: '100%' }}>
      <Flex
        gap={12}
        direction="column"
        style={{
          width: '100%',
        }}
      >
        <InfoItem
          title="항목 이름"
          titleStyle={{
            display: 'inline-block',
            paddingTop: 6,
          }}
          description={
            <Input
              inputRef={inputRef}
              style={{ width: '100%' }}
              value={name}
              onChange={(e) => setName(e.target.value)}
              inputStatus={name.length > CATEGORY_NAME_MAX_LENGTH ? 'warning' : undefined}
              additionalText={
                name.length > CATEGORY_NAME_MAX_LENGTH ? CATEGORY_NAME_MAX_LENGTH_INFO : undefined
              }
            />
          }
        />
        <InfoItem
          title="항목 설명"
          titleStyle={{
            display: 'inline-block',
            paddingTop: 6,
          }}
          description={
            <TextArea
              style={{
                width: '100%',
              }}
              value={description}
              inputStatus={
                description.length > CATEGORY_DESCRIPTION_MAX_LENGTH ? 'warning' : undefined
              }
              onChange={(e) => setDescription(e.target.value)}
              additionalText={
                description.length > CATEGORY_DESCRIPTION_MAX_LENGTH
                  ? CATEGORY_DESCRIPTION_MAX_LENGTH_INFO
                  : undefined
              }
            />
          }
        />

        <Flex justify="flex-end" gap={4}>
          <Button type="button" onClick={onCancel}>
            취소
          </Button>
          <Button buttonType="primary" type="submit" disable={isLoading || !name}>
            만들기
          </Button>
        </Flex>
      </Flex>
    </form>
  );
};

export default GroupNewInfo;
